<script setup>
import { ExpressionEditor } from '@sensehawk/expression-editor';
import { keyBy } from 'lodash-es';
import { computed, inject, ref } from 'vue';
import { useBIDatasetPicker } from '~/bi/composables/useBIDatasetPicker.js';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';

const props = defineProps({
  stage: { type: Object, default: () => ({}) },
  tables: { type: Array, default: () => [] },
  allTables: { type: Array, default: () => [] },
  previousStageFields: { type: Array, default: () => [] },
  stageIndex: { type: Number },
  value: { type: Object },
});

const emit = defineEmits(['save']);
const $t = inject('$t');
const { getIconsForType, getColumnsForSeries, constructFieldName } = useBIQueryBuilder();
const bi_store = useBiStore();

const form_data = ref({
  primary_dataset: null,
  secondary_dataset: null,
  on: [],
  condition: null,
});

const is_menu_open = ref(false);
const join_types = getIconsForType('joins');

const all_tables_map = keyBy(props.allTables, 'name');
const getTableLabel = table_name => all_tables_map[table_name]?.label || table_name;

const conditions = ref([
  { label: '=', uid: '=' },
  { label: '!=', uid: '!=' },
  { label: '>', uid: '>' },
  { label: '<', uid: '<' },
  { label: '>=', uid: '>=' },
  { label: '<=', uid: '<=' },
]);

const types = ref([
  { label: 'Inner Join', description: $t('Keep only the rows that match in both tables.'), uid: 'inner', icon: join_types.inner },
  { label: 'Left Join', uid: 'left', description: $t('Keep all rows from the first table, and bring in matching rows from second table.'), icon: join_types.left },
  { label: 'Right Join', uid: 'right', description: $t('Keep all rows from the second table, and bring in matching rows from first table.'), icon: join_types.right },
  { label: 'Full Join', uid: 'full', description: $t('Keep all rows from both tables, matching where possible.'), icon: join_types.outer },
]);

const selected_type = ref(types.value[0]);
const expression = ref(props.value?.condition || '');
const is_expression = ref(!!props.value?.condition);
const is_loading = ref(false);
const editor_ref = ref(null);

// Initialize when editing an existing join
if (props.value) {
  form_data.value = {
    secondary_dataset: props.allTables.find(table => table.name === props.value.table.name),
    on: props.value.on ? JSON.parse(JSON.stringify(props.value.on)) : [],
    condition: props.value.condition || null,
  };
  selected_type.value = types.value.find(type => type.uid === props.value.type) || types.value[0];
  expression.value = props.value?.condition || '';
  is_expression.value = !!props.value?.condition;
  if (props.value?.table?.generateSeries) {
    addGenerateSeries(props.value?.table);
    form_data.value.on = props.value.on ? JSON.parse(JSON.stringify(props.value.on)) : [];
  }
}

async function save(close) {
  is_loading.value = true;
  try {
    if (is_expression.value) {
      await editor_ref.value.validateCurrentExpression();
      if (!editor_ref.value.isValid)
        return;
    }
    const data = form_data.value;
    await bi_store.getTableColumns({ table_name: data?.secondary_dataset?.name, prefix_alias: true });
    emit('save', {
      table: {
        name: data?.secondary_dataset?.name,
        label: data?.secondary_dataset?.label,
        alias: data?.secondary_dataset?.name,
        generateSeries: data?.secondary_dataset?.generateSeries,
      },
      type: selected_type.value.uid,
      condition: is_expression.value ? expression.value : undefined,
      on: is_expression.value ? undefined : data.on,
    });
    close();
    // reset form
    form_data.value = { primary_dataset: null, secondary_dataset: null, on: [], condition: null };
    expression.value = '';
    is_expression.value = false;
    selected_type.value = types.value[0];
  }
  finally {
    is_loading.value = false;
  }
}

const selectedTables = computed(() => props.tables.filter(table => table.name !== props.value?.table?.name));

const all_selected_columns = computed(() =>
  selectedTables.value
    .map(table => table.columns.map(column => ({ label: column.label, name: column.field, type: column.type, path: column.path })))
    .flat(),
);

const previousStageFieldsArray = computed(() => props.previousStageFields.map(field => ({
  label: field.alias || field.label,
  name: constructFieldName({ label: field.alias, table_name: 'results' }),
  is_aggregation: field.is_aggregation,
  type: field.type,
  path: ['Previous stage'],
})));

const columns = computed(() => {
  const secondaryCols = (form_data.value?.secondary_dataset?.columns || []).map(column => ({ name: column.field, label: column.label, type: column.type, path: [form_data.value?.secondary_dataset?.label, ...(column.path || [])] }));
  return [...previousStageFieldsArray.value, ...all_selected_columns.value, ...secondaryCols];
});

const selected_tables_names = computed(() => selectedTables.value.map(table => table.name));
const tables = computed(() => props.allTables.filter(table => !selected_tables_names.value.includes(table.name)));
const { hierarchicalSchemas } = useBIDatasetPicker(tables);

function secondaryDatasetChanged(value) {
  form_data.value.secondary_dataset = value;
  form_data.value.on = form_data.value.on || [];
  // clear right-side selections
  form_data.value.on.forEach((el) => {
    el.right = null;
    el.right_label = null;
    el.right_table = null;
  });
  // ensure at least one condition row
  if (form_data.value.on.length === 0)
    form_data.value.on.push({ left: null, op: '=', right: null, logic: 'AND' });
}

function onMenuOpened() {
  is_menu_open.value = true;
}
function onMenuClosed() {
  is_menu_open.value = false;
  if (props.value) {
    form_data.value = {
      secondary_dataset: props.allTables.find(table => table.name === props.value.table.name),
      on: props.value.on ? JSON.parse(JSON.stringify(props.value.on)) : [],
      condition: props.value.condition || null,
    };
    expression.value = props.value?.condition;
    is_expression.value = !!props.value?.condition;
    selected_type.value = types.value.find(type => type.uid === props.value.type) || types.value[0];
    if (props.value?.table?.generateSeries) {
      addGenerateSeries(props.value?.table);
      form_data.value.on = props.value.on ? JSON.parse(JSON.stringify(props.value.on)) : [];
    }
  }
  else {
    form_data.value = { primary_dataset: null, secondary_dataset: null, on: [], condition: null };
    is_expression.value = false;
    expression.value = '';
    selected_type.value = types.value[0];
  }
}

const secondary_dataset_table = computed(() => (form_data.value.secondary_dataset ? [form_data.value.secondary_dataset] : []));

function rightColumnSelected(event, index) {
  form_data.value.on[index] = form_data.value.on[index] || {};
  form_data.value.on[index].right = event.field;
  form_data.value.on[index].right_label = event.label;
  if (event.is_bin) {
    form_data.value.on[index].right = { field: event.field, dateBin: event.dateBin };
    form_data.value.on[index].right_label = `${event.label} (${event.dateBin})`;
  }
  form_data.value.on[index].right_table = event.table_name;
}

function leftColumnSelected(event, index) {
  form_data.value.on[index] = form_data.value.on[index] || {};
  form_data.value.on[index].left = event.field;
  form_data.value.on[index].left_label = event.label;
  if (event.is_bin) {
    form_data.value.on[index].left = { field: event.field, dateBin: event.dateBin };
    form_data.value.on[index].left_label = `${event.label} (${event.dateBin})`;
  }
  form_data.value.on[index].left_table = event.table_name || 'Results';
}

function getStageTableName() {
  if (props.stage.table) {
    const table = props.allTables.find(table => table.name === props.stage.table);
    if (table)
      return table.label;
  }
  return 'Previous stage';
}

function getDisabledCondition() {
  if (is_expression.value) {
    return false;
  }
  if (form_data.value.on?.length) {
    const last_condition = form_data.value.on[form_data.value.on.length - 1];
    return !(last_condition.left && last_condition.right);
  }
  else {
    return true;
  }
}

function addGenerateSeries(data) {
  secondaryDatasetChanged({ columns: getColumnsForSeries(data), name: data.name, label: data.name, generateSeries: data.generateSeries });
}
</script>

<template>
  <hawk-menu position="fixed" additional_trigger_classes="w-full flex ring-transparent" @close="onMenuClosed" @open="onMenuOpened">
    <template #trigger>
      <slot>
        <hawk-button
          size="xxs" color="gray" type="light" class="!rounded-md !shadow-none !ring-0"
          :class="{ '!bg-gray-700 !text-white': is_menu_open, '!bg-transparent ': !is_menu_open }"
          icon
        >
          <IconHawkVectorJoin class="size-4" />
        </hawk-button>
      </slot>
    </template>
    <template #content="{ close }">
      <div class="z-20 bg-white border w-[600px] flex flex-col" @keydown.stop @keypress.stop>
        <div>
          <div class="w-full font-medium p-4 flex items-center justify-between border-b">
            <div class="flex items-center gap-2">
              {{ $t('Join datasets') }}
            </div>
            <div class="flex items-center gap-2" @click="close">
              <hawk-button icon type="text">
                <IconHawkXClose class="text-gray-500 size-4" />
              </hawk-button>
            </div>
          </div>
          <div class="flex-1 p-4">
            <div class="col-span-12">
              <div class="text-sm font-medium">
                {{ $t('Join') }}
              </div>
              <div class="grid grid-cols-12 items-center mt-2 ">
                <div class="col-span-5 text-sm font-normal border rounded-lg bg-gray-200 p-2 flex items-center justify-between">
                  <div class="opacity-50">
                    {{ getStageTableName() }}
                  </div>
                  <div>
                    <IconHawkChevronDown class="size-4 transition-transform" :class="{ 'rotate-180': open }" />
                  </div>
                </div>
                <hawk-menu position="fixed" class="col-span-2 text-center" :items="types" @select="selected_type = $event">
                  <template #trigger>
                    <hawk-button type="text" icon>
                      <component :is="selected_type.icon" class="size-5" />
                    </hawk-button>
                  </template>
                  <template #item="{ item }">
                    <div class="flex">
                      <component :is="item.icon" class="size-5 mr-2 mt-[1px]" />
                      <div class="text-left max-w-[260px] break-words">
                        <div class="text-sm font-medium text-gray-700">
                          {{ item.label }}
                        </div>
                        <div class="text-xs font-normal text-gray-600">
                          {{ item.description }}
                        </div>
                      </div>
                    </div>
                  </template>
                </hawk-menu>
                <bi-query-builder-table-picker
                  class="col-span-5"
                  :hierarchical-schemas="hierarchicalSchemas"
                  :model-value="form_data.secondary_dataset"
                  @update:model-value="secondaryDatasetChanged"
                >
                  <template #footer="{ close: closeDatasetMenu }">
                    <hawk-menu
                      additional_trigger_classes="w-full flex ring-transparent"
                      position="fixed"
                    >
                      <template #trigger>
                        <div class="text-sm font-medium flex py-2 px-1 text-primary-700 hover:bg-gray-50 cursor-pointer rounded-lg items-center">
                          <IconHawkTrendUpOne class="text-primary-600 mr-2" />
                          {{ $t('Generate series') }}
                        </div>
                      </template>
                      <template #content="{ close: closeMenu }">
                        <bi-dataset-picker-series :is-menu="true" class="w-[400px] font-normal text-sm" @save="addGenerateSeries($event);closeMenu();closeDatasetMenu()" @keydown.stop @keypress.stop />
                      </template>
                    </hawk-menu>
                  </template>
                </bi-query-builder-table-picker>
              </div>
              <div v-show="form_data.secondary_dataset" class="mt-4" :class="{ 'max-h-[260px] overflow-y-scroll scrollbar -mr-4 pr-4': !is_expression }">
                <div class="flex  flex justify-between items-center">
                  <span class="text-sm font-semibold text-gray-900">
                    {{ $t('Conditions') }}
                  </span>
                  <div class="flex">
                    <hawk-button type="text" icon size="xs" class="hover:!bg-gray-100" :class="{ 'bg-gray-200': !is_expression }" @click="is_expression = false">
                      <IconHawkList class="text-gray-600 size-4" />
                    </hawk-button>
                    <hawk-button type="text" icon size="xs" class="hover:!bg-gray-100" :class="{ 'bg-gray-200': is_expression }" @click="is_expression = true">
                      <IconHawkFormula class="text-gray-600 size-4" />
                    </hawk-button>
                  </div>
                </div>
                <div v-show="is_expression">
                  <ExpressionEditor
                    ref="editor_ref"
                    v-model="expression"
                    class="mt-1 text-gray-700 text-sm font-normal"
                    :columns="columns"
                    :filter-expressions="['filters']"
                  />
                  <div class="text-red-700 text-sm">
                    {{ editor_ref?.errors?.[0]?.message }}
                  </div>
                </div>
                <div v-if="!is_expression">
                  <div v-for="(cond, index) in form_data.on" :key="index">
                    <div v-if="index === 0" class=" text-sm font-medium">
                      {{ $t('on') }}
                    </div>
                    <div v-else class="grid items-center max-w-[100px] ">
                      <hawk-menu
                        class="mt-3 mb-1"
                        position="fixed"
                        :items="[{ value: 'AND', label: $t('AND') }, { value: 'OR', label: $t('OR') }]"
                        @select="(e) => (form_data.on[index].logic = e.value)"
                      >
                        <template #trigger>
                          <div class="flex items-center">
                            <div class="text-sm font-medium">
                              {{ form_data.on?.[index]?.logic }}
                            </div>
                            <div class="ml-2">
                              <IconHawkChevronDown class="size-4" />
                            </div>
                          </div>
                        </template>
                      </hawk-menu>
                    </div>
                    <div class="grid grid-cols-12 col-span-12 items-center mt-2 relative group">
                      <bi-query-builder-joins-field class="col-span-5" :tables="selectedTables" :previous-stage-fields="previousStageFields" :stage-index="stageIndex" @selected="(e) => leftColumnSelected(e, index)">
                        <template v-if="form_data.on?.[index]?.left" #default>
                          <div class="text-gray-500 text-xs">
                            {{ getTableLabel(form_data.on?.[index]?.left_table) }}
                          </div>
                          <div class="text-sm font-medium text-gray-900">
                            {{ form_data.on?.[index]?.left_label }}
                          </div>
                        </template>
                      </bi-query-builder-joins-field>

                      <hawk-menu position="fixed" class="col-span-2 text-center" :items="conditions" @select="(e) => (form_data.on[index].op = e.uid)">
                        <template #trigger>
                          <hawk-button type="text" icon>
                            {{ form_data.on?.[index]?.op || '=' }}
                          </hawk-button>
                        </template>
                      </hawk-menu>
                      <bi-query-builder-joins-field class="col-span-5 pr-2" :tables="secondary_dataset_table" :stage-index="stageIndex" @selected="(e) => rightColumnSelected(e, index)">
                        <template v-if="form_data.on?.[index]?.right" #default>
                          <div class="text-gray-500  text-xs">
                            {{ getTableLabel(form_data.on?.[index]?.right_table) }}
                          </div>
                          <div class="text-sm font-medium text-gray-900">
                            {{ form_data.on?.[index]?.right_label }}
                          </div>
                        </template>
                      </bi-query-builder-joins-field>
                      <div type="text" icon class="hidden group-hover:block !absolute top-3.5 -right-3 cursor-pointer" @click="form_data.on.splice(index, 1)">
                        <IconHawkTrashThree class="text-gray-500 text-xs size-4" />
                      </div>
                    </div>
                  </div>
                  <hawk-button type="text" class="mt-1" :disabled="form_data.on.length && !(form_data.on?.[form_data.on.length - 1]?.right && form_data.on?.[form_data.on.length - 1]?.left)" @click="form_data.on[form_data.on.length] = { op: '=', logic: 'AND' }">
                    <IconHawkPlus />
                    {{ $t('Add condition') }}
                  </hawk-button>
                </div>
              </div>
            </div>
            <div class="flex w-full items-center justify-between p-4 border-t mt-4">
              <div />
              <div class="flex items-center gap-2">
                <hawk-button type="outlined" @click="close">
                  {{ $t('Cancel') }}
                </hawk-button>
                <hawk-button name="submit" :disabled="getDisabledCondition()" :loading="is_loading" @click="save(close)">
                  {{ $t('Save') }}
                </hawk-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </hawk-menu>
</template>
